// Declare global window property for platform detection
declare global {
  interface Window {
    detectedPlatform?: string;
  }
}

export enum Platform {
  TELEGRAM = 'telegram',
  MAX = 'max',
  UNKNOWN = 'unknown',
}

/**
 * User data structure that works for both Telegram and MAX
 */
export interface UserData {
  id: number | string;
  firstName: string;
  lastName?: string;
  username?: string;
  platform: Platform;
}

/**
 * Init data structure for WebApp
 */
export interface WebAppInitData {
  user?: UserData;
  authDate: number;
  hash: string;
  chatInstance?: string;
  [key: string]: any;
}

/**
 * Platform detector service
 */
export const platformDetector = {
  /**
   * Detect which platform the app is running on
   * Uses early detection from index.html as primary source
   */
  detect(): Platform {
    if (typeof window === 'undefined') {
      return Platform.UNKNOWN;
    }

    // Primary: Use early platform detection from index.html
    // This is set before any SDKs load, based on URL parameters
    if (window.detectedPlatform === 'TELEGRAM') {
      return Platform.TELEGRAM;
    }
    if (window.detectedPlatform === 'MAX') {
      return Platform.MAX;
    }

    // Fallback: Check for actual SDK objects (in case early detection wasn't available)
    // Check for Telegram WebApp
    if (window.Telegram?.WebApp?.initData) {
      return Platform.TELEGRAM;
    }

    // Check for MAX Mini App (window.WebApp is the correct object according to MAX docs)
    if (window.WebApp?.initData) {
      return Platform.MAX;
    }

    return Platform.UNKNOWN;
  },

  /**
   * Get human-readable platform name
   */
  getPlatformName(): string {
    const platform = this.detect();
    switch (platform) {
      case Platform.TELEGRAM:
        return 'Telegram';
      case Platform.MAX:
        return 'MAX';
      default:
        return 'Unknown';
    }
  },

  /**
   * Check if current platform is supported
   */
  isSupported(): boolean {
    return this.detect() !== Platform.UNKNOWN;
  },

  /**
   * Check if running in Telegram
   */
  isTelegram(): boolean {
    return this.detect() === Platform.TELEGRAM;
  },

  /**
   * Check if running in MAX
   */
  isMax(): boolean {
    return this.detect() === Platform.MAX;
  },

  /**
   * Get platform info for debugging
   */
  getDebugInfo(): Record<string, any> {
    return {
      platform: this.detect(),
      platformName: this.getPlatformName(),
      isSupported: this.isSupported(),
      earlyDetection: typeof window !== 'undefined' ? window.detectedPlatform : null,
      urlHash: typeof window !== 'undefined' ? window.location.hash : null,
      hasUserAgent: typeof navigator !== 'undefined',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      telegramAvailable: typeof window !== 'undefined' && !!window.Telegram?.WebApp,
      maxAvailable: typeof window !== 'undefined' && !!window.WebApp,
    };
  },
};
